import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { INDUSTRIES_ALL } from "@/data/content";
import { PageHero } from "./Services";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

export default function Industries() {
  return (
    <div data-testid="industries-page" className="bg-white">
      <Seo
        title="Industries We Serve"
        description="Specialized language and AI services for Healthcare, Legal, Finance, Government, Technology, Education, Media, E-commerce, and Travel — domain expertise where it matters."
        path="/industries"
      />

      <PageHero
        eyebrow="Industries"
        title="Specialists, not generalists."
        subtitle="Nine core industries — each served by native linguists with verified domain experience. Click any sector for the full scope of work."
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES_ALL.map((ind, i) => (
              <Reveal variant="up" delay={(i % 3) + 1} key={ind.slug}>
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`industry-card-${ind.slug}`}
                  className="ent-card overflow-hidden group flex flex-col h-full"
                >
                  <div className="img-zoom h-44 bg-[#F7F8FA] relative">
                    <img src={ind.image} alt={ind.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#071D49]/55 via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 text-[10px] font-semibold tracking-[0.22em] text-white/95 bg-[#071D49]/70 backdrop-blur px-2 py-1 rounded">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-[17px] font-semibold text-[#071D49] group-hover:text-[#1A4E8A] transition-colors leading-tight">
                      {ind.title}
                    </h3>
                    <p className="mt-2 text-[12.5px] text-[#5B6472] leading-snug">{ind.short}</p>
                    <p className="mt-4 text-[13px] text-[#5B6472] leading-relaxed line-clamp-3 flex-1">
                      {ind.intro}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1A4E8A] group-hover:text-[#00A990] transition-colors">
                      Explore sector <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DOMAIN EXPERTISE */}
      <section className="py-14 lg:py-16 bg-[#F7F8FA] border-y border-[#E5E7EB]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal variant="right">
            <p className="eyebrow">Why it matters</p>
            <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              A clinical trial protocol is not a marketing brochure.
            </h2>
            <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed">
              Every Petraloc linguist is selected and verified for a specific industry vertical.
              We don't hand a medical document to a general translator, and we don't ask a
              contract specialist to localize a video game.
            </p>
          </Reveal>
          <Reveal variant="left">
            <ul className="space-y-3">
              {[
                "Verified domain credentials — medical, legal, financial, technical",
                "Industry-specific terminology databases & translation memories",
                "Regulatory compliance: HIPAA, GDPR, ISO 17100, ATA, USCIS",
                "Confidentiality agreements signed on every engagement",
                "Independent second-pass review by a native specialist",
              ].map((line, i) => (
                <li key={line} className={`flex items-start gap-3 text-[14px] text-[#222222] r-init r-up ${i < 5 ? `d-${i + 1}` : ""}`}>
                  <Check className="w-4 h-4 mt-1 text-[#00D1B2] flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
